import {
  CalculationResult,
  Inputs,
  YEAR_ONE_MONTHS,
  calculateModel,
  getLeaderMonths,
  getRampShares,
} from './roiCalculations'
import { managerView, overrideSchedule } from './commissions'
import { repCashByMonth, RepScenario } from './repEconomics'

/** Long enough for every account's backend and residuals to land. */
const TRAIL_MONTHS = 36

export interface OverrideBreakdown {
  upfront: number
  backend: number
  clawbacks: number
}

export interface PestRepRow {
  key: string
  label: string
  detail: string
  /** Accounts the rep writes. */
  accounts: number
  /** Cumulative revenue from those accounts (company view). */
  revenue: number
  /** Gross profit (company) or total override income (manager). */
  profit: number
  /** Profit ÷ cost to recruit the rep. */
  multiple: number
  /** Manager view: where the override income comes from. */
  overrides?: OverrideBreakdown
}

export interface PestRepEconomics {
  mode: 'company' | 'manager'
  /** Year-one recruiting spend: implementation, fees, ads, est. bonuses. */
  spend: number
  signed: number
  /** Year-one signs expected to clear day 90. */
  day90Reps: number
  costPerDay90Rep: number
  hiresPerDay90Rep: number
  /** Company view: months the rep sells in year one. */
  sellingMonths: number
  rows: PestRepRow[]
  /** Company view: months from the rep's first sale to cover their cost. */
  paybackMonths: number | null
}

// Pest and Summer Pest rep economics.
//
// Cost per day-90 rep = everything spent recruiting in year one
// (implementation, D2D HIRE fees, ads, estimated bonuses) ÷ the year-one
// signs expected to clear day 90. Every washout is paid for in that number.
// The leader search is left out.
//
// Manager view: what one rep's accounts pay the manager in total —
// upfront overrides, plus the backend paid retroactively on accounts still
// in good standing once the clawback window closes, minus clawbacks — for a
// rep who sells through day 90 (their 3 ramp months) and for one who sells
// 6 months.
//
// Company view: one rep who clears day 90 and sells only in year one (the
// season, for Summer Pest), then the same accounts paying out with no new
// sales. Cumulative gross profit at the end of years one, two and three.
//
// Scenarios: 50% Sales halves output; 2x Attrition halves the reps who clear
// day 90 (and their bonuses), so each one costs twice as much.
export function calculatePestRepEconomics(
  inputs: Inputs,
  result: CalculationResult,
  scenario: RepScenario = {},
): PestRepEconomics | null {
  const model =
    getLeaderMonths(inputs) > 0
      ? calculateModel({
          ...inputs,
          leaderCampaignMonths: 0,
          leaderMonth2Sales: undefined,
        })
      : result
  const rows = model.rows
  const yearOne = rows.slice(1, YEAR_ONE_MONTHS + 1)
  if (yearOne.length === 0) return null

  const attrition = scenario.doubleAttrition ? 0.5 : 1
  const signed = sum(yearOne.map((r) => r.hires))
  const launched = sum(yearOne.map((r) => r.launched))
  const day90Reps =
    launched * clampPct(inputs.pct30) * clampPct(inputs.pct90) * attrition
  const bonuses = sum(yearOne.map((r) => r.bonuses))
  const spend =
    (rows[0]?.totalCost ?? 0) +
    sum(yearOne.map((r) => r.totalCost)) -
    bonuses * (1 - attrition)
  if (day90Reps <= 0 || spend <= 0) return null
  const cost = spend / day90Reps

  const manager = managerView(inputs)
  const sellingMonths = seasonMonths(inputs)
  const rowsOut: PestRepRow[] = []
  let paybackMonths: number | null = null

  if (manager) {
    const scenarios = [
      {
        key: 'day90',
        label: 'Clears day 90',
        detail: 'Sells 3 months, then stops',
        months: 3,
      },
      {
        key: '6mo',
        label: 'Sells 6 months',
        detail: 'Ramp, then 3 months at the veteran rate',
        months: 6,
      },
    ]
    for (const s of scenarios) {
      // Placed on the real calendar so paused sales months write nothing,
      // and the backend lands in the actual payout month.
      const { sales, pausedCount } = repSalesOnCalendar(
        inputs,
        scenario,
        s.months,
      )
      const schedule = overrideSchedule(inputs, sales)
      const upfront = sum(schedule.upfront)
      const backend = sum(schedule.backend)
      const clawbacks = sum(schedule.clawbacks)
      const profit = upfront + backend - clawbacks
      rowsOut.push({
        key: s.key,
        label: s.label,
        detail:
          pausedCount > 0
            ? `${s.months} months in the field, ${pausedCount} paused`
            : s.detail,
        accounts: sum(sales),
        revenue: sum(repCashByMonth(inputs, sales).revenue),
        profit,
        multiple: profit / cost,
        overrides: { upfront, backend, clawbacks },
      })
    }
  } else {
    const sales = repSales(inputs, scenario, sellingMonths)
    const { revenue, profit } = repCashByMonth(inputs, sales)
    const accounts = sum(sales)
    ;[1, 2, 3].forEach((year) => {
      const months = year * 12
      const p = sum(profit.slice(0, months))
      rowsOut.push({
        key: `y${year}`,
        label: `Year ${year}`,
        detail:
          year === 1
            ? `Sells ${sellingMonths} month${sellingMonths === 1 ? '' : 's'}`
            : 'Same accounts, no new sales',
        accounts,
        revenue: sum(revenue.slice(0, months)),
        profit: p,
        multiple: p / cost,
      })
    })
    let running = 0
    for (let m = 0; m < profit.length; m++) {
      running += profit[m]
      if (running >= cost) {
        paybackMonths = m + 1
        break
      }
    }
  }

  return {
    mode: manager ? 'manager' : 'company',
    spend,
    signed,
    day90Reps,
    costPerDay90Rep: cost,
    hiresPerDay90Rep: signed / day90Reps,
    sellingMonths,
    rows: rowsOut,
    paybackMonths,
  }
}

/**
 * Realized accounts one rep writes, month by month from their first sale:
 * the Mo 1–3 ramp, then the full veteran rate, for `sellMonths` months.
 */
function repSales(
  inputs: Inputs,
  scenario: RepScenario,
  sellMonths: number,
): number[] {
  const vet = Math.max(0, inputs.salesVetMonthly)
  const ramp = getRampShares(inputs)
  const factor = clampPct(inputs.pullthrough) * (scenario.halfSales ? 0.5 : 1)
  return Array.from({ length: TRAIL_MONTHS }, (_, m) => {
    if (m >= sellMonths) return 0
    return (m < 3 ? vet * ramp[m] : vet) * factor
  })
}

/**
 * Manager view: one rep's accounts on the model's calendar, starting at the
 * launch month (or Month 1) for `fieldMonths` months in the field. Paused
 * sales months write nothing (the ramp still moves on), so they shrink the
 * upfront and the backend, and the backend lands in the real payout month.
 */
function repSalesOnCalendar(
  inputs: Inputs,
  scenario: RepScenario,
  fieldMonths: number,
): { sales: number[]; pausedCount: number } {
  const vet = Math.max(0, inputs.salesVetMonthly)
  const ramp = getRampShares(inputs)
  const factor = clampPct(inputs.pullthrough) * (scenario.halfSales ? 0.5 : 1)
  const start = launchIndex(inputs)
  const paused = new Set(inputs.pausedSalesMonths || [])
  const sales = Array(start + TRAIL_MONTHS).fill(0) as number[]
  let pausedCount = 0
  for (let k = 0; k < fieldMonths; k++) {
    const m = start + k
    if (paused.has(m)) {
      pausedCount++
      continue
    }
    sales[m] = (k < 3 ? vet * ramp[k] : vet) * factor
  }
  return { sales, pausedCount }
}

/** 0-based model month the rep's first sale lands in. */
function launchIndex(inputs: Inputs): number {
  return Number(inputs.launchMonth) >= 1
    ? Math.min(YEAR_ONE_MONTHS, Math.round(Number(inputs.launchMonth))) - 1
    : 0
}

/**
 * Months a rep can sell in year one: from the launch month (or Month 1) to
 * Month 12, skipping paused sales months. Pest with no pauses: 12.
 */
function seasonMonths(inputs: Inputs): number {
  const start = launchIndex(inputs)
  const paused = new Set(inputs.pausedSalesMonths || [])
  let count = 0
  for (let m = start; m < YEAR_ONE_MONTHS; m++) {
    if (!paused.has(m)) count++
  }
  return Math.max(1, count)
}

function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0)
}

function clampPct(value: number): number {
  return Math.min(1, Math.max(0, (value || 0) / 100))
}
